/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

/*Bearer ${token}` 
  All of the routes for the Vision UI Dashboard React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Vision UI Dashboard React layouts
import Dashboard from "layouts/dashboard";
import MainDashboard from "layouts/maindashboard";
import SignIn from "layouts/authentication/sign-in";
import VisitorPortal from "layouts/visitorportal";
import AgentAuthCallback from "layouts/AgentAuthCallback";
import SamlCallback from "layouts/authentication/saml-callback";
import Unauthorized from "layouts/unauthorized";
import TenantUsers from "layouts/tenantusers";
import Organisations from "layouts/organisations";
import RoleAssignmentPage from "layouts/roleassignment";
import LicenseManagement from "layouts/licensemanagement";
import SSOSettings from "layouts/ssosettings";
import BankFeeds from "layouts/bankfeeds";
import ConsentAuditLog from "layouts/bankfeeds/ConsentAuditLog";
import ClientProfiles from "layouts/clientprofile";
import ClientProfileDetailPage from "layouts/clientprofiledetail";
import CustomerReviewPage from "layouts/csreview";
import IndividualClients from "layouts/individualclients";
import IndividualClientDetail from "layouts/individualclientdetail";
import JobsBoard from "layouts/jobsboard";
import Directors from "layouts/directors";
import ChangePassword from "layouts/changepassword";
import Exceptions from "layouts/exceptions";
import PeriodClose from "layouts/periodclose";
import VatReturn from "layouts/vatreturn";
import VATPeriodsPage from "layouts/vatperiods";
import VATDashboard from "layouts/vatdashboard";
import VATSubmissionPage from "layouts/vatsubmission";
import Reports from "layouts/reports";
import Advisory from "layouts/advisory";
import NotificationsPage from "layouts/notifications";
import EmailConfigPage from "layouts/emailconfig";
import EmailInboxPage from "layouts/emailinbox";
import TasksPage from "layouts/tasks";
import BackupRestore from "layouts/backuprestore";
import { ROLES } from "context/AuthzContext";
import { PERMISSIONS } from "auth/permissions";

// Vision UI Dashboard React icons
import { IoIosDocument } from "react-icons/io";
import { BsFillPersonFill, BsPeopleFill } from "react-icons/bs";
import { IoBuild } from "react-icons/io5";
import { IoHome, IoBarChart, IoCalendar, IoDocument, IoBulb, IoBookOutline, IoNotifications, IoMailOutline, IoListOutline, IoSettings, IoHelpCircleOutline, IoCloudOutline } from "react-icons/io5";
import { IoCloudUploadOutline, IoDocumentTextOutline } from "react-icons/io5";
import { FiAlertTriangle } from "react-icons/fi";
import { MdBugReport } from "react-icons/md";
import Icon from "@mui/material/Icon";
import AccountsPrepWorkflow from "layouts/accountsprepworkflow";
import AppSettings from "layouts/appsettings";
import HealthCheckPage from "layouts/healthcheck";
import IssuesFixesPage from "layouts/issuesfixes";
import IntegrationsPage from "layouts/integrations";
import LegalPage from "layouts/legal";
import AiUsage from "layouts/aiusage";
import AiFeedbackReview from "layouts/aifeedbackreview";

const AUTHENTICATED_ROLES = [ROLES.TENANT_ADMIN, ROLES.USER];

const routes = [
  { type: "hidden", key: "cs-review", route: "/cs-review/:token", component: CustomerReviewPage, public: true },

  // ── Dashboard ──────────────────────────────────────────────────────────────
  {
    type: "collapse",
    name: "Dashboard",
    key: "main-dashboard",
    route: "/dashboard",
    icon: <IoHome size="15px" color="inherit" />,
    component: MainDashboard,
    allowedRoles: [ROLES.TENANT_ADMIN, ROLES.USER],
    noCollapse: true,
  },

  // ── Profile (moved next to Dashboard) ──────────────────────────────────────
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    route: "/client-profiles",
    icon: <Icon sx={{ fontSize: '15px', color: '#ffffff' }}>person</Icon>,
    component: ClientProfiles,
    allowedRoles: [ROLES.TENANT_ADMIN, ROLES.USER],
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Administration",
    key: "administration",
    icon: <BsPeopleFill size="15px" color="inherit" />,
    // Host + Tenant admins
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
    collapse: [
      {
        type: "collapse",
        name: "User Management",
        key: "tenant-users",
        route: "/tenant-users",
        icon: <BsFillPersonFill size="15px" color="inherit" />,
        component: TenantUsers,
        // Allow tenant admins to manage users as well (per-tenant)
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Role Management",
        key: "role-assignment",
        route: "/role-assignment",
        icon: <BsFillPersonFill size="15px" color="inherit" />,
        component: RoleAssignmentPage,
        // Super admins and tenant admins may manage tenant role assignments
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Licenses",
        key: "licenses",
        route: "/licenses",
        icon: <IoDocumentTextOutline size="15px" color="inherit" />,
        component: LicenseManagement,
        allowedRoles: [ROLES.SUPER_ADMIN],
        noCollapse: true,
      },
    ],
  },

  {
    type: "collapse",
    name: "Emails",
    key: "email-inbox",
    route: "/email-inbox",
    icon: <IoMailOutline size="15px" color="inherit" />,
    component: EmailInboxPage,
    // Host admin only: limit Emails main menu to SUPER_ADMIN (host)
    allowedRoles: [ROLES.SUPER_ADMIN],
    noCollapse: true,
  },

  // Hidden client routes (kept for direct navigation)
  { type: "hidden", key: "jobs-board", name: "Jobs Board", route: "/jobs-board", component: JobsBoard, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.USER] },
  { type: "hidden", key: "tasks", name: "Tasks", route: "/tasks", component: TasksPage, allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "change-password", name: "Change Password", route: "/change-password", component: ChangePassword, allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.USER] },

  // ── Accounting & Bank Feeds (hidden — routes kept for direct navigation) ────
  { type: "hidden", key: "exceptions",      name: "Exceptions",        route: "/exceptions",      component: Exceptions,      allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "period-close",    name: "Period Close",      route: "/period-close",    component: PeriodClose,     allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "vat-dashboard",   name: "VAT Dashboard",     route: "/vat-dashboard",   component: VATDashboard,    allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "vat-return",      name: "VAT Return",        route: "/vat-return",      component: VatReturn,       allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "vat-periods",     name: "VAT Periods",       route: "/vat-periods",     component: VATPeriodsPage,  allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "reports",         name: "Reports",           route: "/reports",         component: Reports,         allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "advisory",        name: "Advisory",          route: "/advisory",        component: Advisory,        allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "bank-feeds",      name: "Bank Feeds",        route: "/bank-feeds",      component: BankFeeds,       allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "consent-audit-log", name: "Consent Audit Log", route: "/consent-audit-log", component: ConsentAuditLog, allowedRoles: AUTHENTICATED_ROLES },

  // ── System ─────────────────────────────────────────────────────────────────
  { type: "title", title: "System", key: "system-title" },
  {
    type: "collapse",
    name: "System Management",
    key: "system-management",
    icon: <IoBuild size="15px" color="inherit" />,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
    collapse: [
      {
        type: "collapse",
        name: "Organisations",
        key: "organisations",
        route: "/organisations",
        icon: <BsFillPersonFill size="15px" color="inherit" />,
        component: Organisations,
        allowedRoles: [ROLES.SUPER_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "SSO Settings",
        key: "sso-settings",
        route: "/sso-settings",
        icon: <IoSettings size="15px" color="inherit" />,
        component: SSOSettings,
        allowedRoles: [ROLES.TENANT_ADMIN],
        requiredPermissions: [PERMISSIONS.MANAGE_SSO],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Notifications",
        key: "notifications",
        route: "/notifications",
        icon: <IoNotifications size="15px" color="inherit" />,
        component: NotificationsPage,
        allowedRoles: [ROLES.TENANT_ADMIN, ROLES.USER],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Settings",
        key: "settings",
        route: "/settings",
        icon: <IoSettings size="15px" color="inherit" />,
        component: AppSettings,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.USER],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "AI Usage",
        key: "ai-usage",
        route: "/ai-usage",
        icon: <IoBarChart size="15px" color="inherit" />,
        component: AiUsage,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "AI Corrections",
        key: "ai-corrections",
        route: "/ai-corrections",
        icon: <IoDocument size="15px" color="inherit" />,
        component: AiFeedbackReview,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Backup & Restore",
        key: "backup-restore",
        route: "/backup-restore",
        icon: <IoCloudUploadOutline size="15px" color="inherit" />,
        component: BackupRestore,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
    ],
  },

  // ── Support ────────────────────────────────────────────────────────────────
  { type: "title", title: "Support", key: "support-title" },
  {
    type: "collapse",
    name: "Support",
    key: "support",
    icon: <IoHelpCircleOutline size="15px" color="inherit" />,
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
    collapse: [
      {
        type: "collapse",
        name: "Health Check",
        key: "health-check",
        route: "/health-check",
        icon: <IoBuild size="15px" color="inherit" />,
        component: HealthCheckPage,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Issues & Fixes",
        key: "issues-fixes",
        route: "/issues-fixes",
        icon: <MdBugReport size="15px" color="inherit" />,
        component: IssuesFixesPage,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
      {
        type: "collapse",
        name: "Integrations",
        key: "integrations",
        route: "/integrations",
        icon: <IoCloudOutline size="15px" color="inherit" />,
        component: IntegrationsPage,
        allowedRoles: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN],
        noCollapse: true,
      },
    ],
  },

  // ── Sign In (shown in sidenav when logged out) ─────────────────────────────
  {
    type: "hidden",
    name: "Sign In",
    key: "sign-in",
    route: "/authentication/sign-in",
    icon: <IoIosDocument size="15px" color="inherit" />,
    component: SignIn,
    public: true,
    noCollapse: true,
  },

  // ── Hidden routes (no sidenav entry) ──────────────────────────────────────
  { type: "hidden", key: "visitor-portal",              name: "Visitor Portal",        route: "/visitor-portal",                  component: VisitorPortal,             allowedRoles: [ROLES.VISITOR], public: false },
  { type: "hidden", key: "privacy-policy",               name: "Privacy Policy",        route: "/legal/privacy-policy",             component: LegalPage,                public: true },
  { type: "hidden", key: "terms-and-conditions",        name: "Terms & Conditions",    route: "/legal/terms-and-conditions",       component: LegalPage,                public: true },
  { type: "hidden", key: "security-contact",            name: "Security Contact",      route: "/legal/security-contact",          component: LegalPage,                public: true },
  { type: "hidden", key: "callback",                     name: "Callback",              route: "/callback",                         component: AgentAuthCallback,         allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "saml-callback", name: "SSO Callback", route: "/authentication/saml-callback", component: SamlCallback, public: true },
  { type: "hidden", key: "unauthorized",                 name: "Unauthorized",          route: "/unauthorized",                     component: Unauthorized,              public: true },
  { type: "hidden", key: "directors",                    name: "Directors",             route: "/directors",                        component: Directors,                 allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "client-profile-detail",        name: "Client Profile",        route: "/client-profiles/:profileId",       component: ClientProfileDetailPage,   allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "individual-client-detail",     name: "Individual Client",     route: "/individual-clients/:clientId",     component: IndividualClientDetail,    allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "vat-submission",               name: "VAT Submission",        route: "/vat-submission",             component: VATSubmissionPage,         allowedRoles: AUTHENTICATED_ROLES },
  { type: "hidden", key: "accounts-prep-workflow",       name: "Accounts Prep",         route: "/accounts-prep/:profileId",         component: AccountsPrepWorkflow,      allowedRoles: AUTHENTICATED_ROLES },
];

export default routes;


